import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getMultiFactorResolver,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  type MultiFactorError,
  type MultiFactorResolver,
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuth } from './AuthProvider';
import { Button, Field, TextField } from '../../components';
import { AuthLayout } from './AuthLayout';
import { GoogleButton } from './GoogleButton';
import { TotpChallenge } from './totp';
import { useT } from '../../lib/i18n';

/**
 * Sign in, by password or by magic link.
 *
 * Both land in the same place. The link path stores the address locally so the
 * completion step does not have to ask for it again, which is the part of magic
 * links people usually get wrong.
 */

const EMAIL_KEY = 'trackilero.signInEmail';
const NEXT_KEY = 'trackilero.signInNext';

/** Only an in-app path is honoured, so a crafted link cannot send someone off-site after sign-in. */
function safeNext(value: string | null): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

export function SignIn() {
  const t = useT();
  const navigate = useNavigate();
  const { status } = useAuth();

  const [searchParams] = useSearchParams();
  // "Start free" on the landing page arrives in link mode: a new person has
  // no password yet, and a sign-in link creates the account on first use.
  const [mode, setMode] = useState<'password' | 'link'>(
    searchParams.get('mode') === 'link' ? 'link' : 'password',
  );
  const next = safeNext(searchParams.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // An account with a second factor stops here mid-door; the code finishes it.
  const [mfa, setMfa] = useState<MultiFactorResolver | null>(null);

  // Completing a magic link: the URL carries the credential.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const stored = window.localStorage.getItem(EMAIL_KEY);
    const address = stored ?? window.prompt('Confirm the email address this link was sent to') ?? '';
    if (!address) return;

    setBusy(true);
    signInWithEmailLink(auth, address, window.location.href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_KEY);
        const stored = safeNext(window.localStorage.getItem(NEXT_KEY));
        window.localStorage.removeItem(NEXT_KEY);
        navigate(stored, { replace: true });
      })
      .catch((err) => {
        if ((err as { code?: string }).code === 'auth/multi-factor-auth-required') {
          setMfa(getMultiFactorResolver(auth, err as MultiFactorError));
        } else {
          setError('That sign-in link has expired. Ask for a new one.');
        }
      })
      .finally(() => setBusy(false));
  }, [navigate]);

  if (status === 'signed-in') return <Navigate to={next} replace />;

  const reset = async () => {
    setError(null);
    if (!email.trim()) return setError(t('auth.emailInvalid'));
    try {
      await sendPasswordResetEmail(auth, email.trim(), { url: `${window.location.origin}/sign-in` });
    } catch {
      // Same outcome either way: an address that is not ours gets the same
      // sentence as one that is.
    }
    setResetSent(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'password') {
        await signInWithEmailAndPassword(auth, email.trim(), password);
        navigate(next, { replace: true });
      } else {
        await sendSignInLinkToEmail(auth, email.trim(), {
          url: `${window.location.origin}/sign-in`,
          handleCodeInApp: true,
        });
        window.localStorage.setItem(EMAIL_KEY, email.trim());
        if (next !== '/') window.localStorage.setItem(NEXT_KEY, next);
        setLinkSent(true);
      }
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        setMfa(getMultiFactorResolver(auth, err as MultiFactorError));
        setBusy(false);
        return;
      }
      // Deliberately the same message either way: telling an anonymous visitor
      // which half was wrong tells them which addresses have accounts.
      setError(
        code === 'auth/too-many-requests'
          ? 'Too many attempts. Wait a few minutes and try again.'
          : 'That email and password do not match an account.',
      );
    } finally {
      setBusy(false);
    }
  };

  const signUpHref = next === '/' ? '/sign-up' : `/sign-up?next=${encodeURIComponent(next)}`;

  return (
    <AuthLayout title={t('auth.signInTitle')} lede={t('auth.signInLede')}>
      <>
        {mfa ? (
          <TotpChallenge resolver={mfa} onDone={() => navigate(next, { replace: true })} />
        ) : linkSent ? (
          <div className="flex flex-col gap-2">
            <p className="text-row font-semibold">Check your email</p>
            <p className="text-pretty text-meta text-muted">
              A sign-in link is on its way to {email}. It works once, and expires in an hour.
            </p>
            <Button
              variant="text"
              className="mt-2 self-start"
              onClick={() => setLinkSent(false)}
            >
              Use a different address
            </Button>
          </div>
        ) : (
          <>
          <GoogleButton next={next} />
          <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
            <Field label="Email" htmlFor="email">
              <TextField
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </Field>

            {mode === 'password' && (
              <Field label="Password" htmlFor="password">
                <TextField
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
            )}

            {error && (
              <p role="alert" className="text-meta-sm text-alert">
                {error}
              </p>
            )}
            {resetSent && (
              <p role="status" className="text-meta-sm text-muted">
                {t('auth.resetSent')}
              </p>
            )}

            <Button type="submit" disabled={busy} className="mt-1 justify-center">
              {mode === 'password' ? t('action.signIn') : 'Email me a sign-in link'}
            </Button>

            {mode === 'password' && (
              <Button variant="text" className="justify-center" onClick={reset}>
                {t('auth.forgotPassword')}
              </Button>
            )}

            <Button
              variant="text"
              className="justify-center"
              onClick={() => {
                setMode(mode === 'password' ? 'link' : 'password');
                setError(null);
              }}
            >
              {mode === 'password' ? 'Sign in with a link instead' : 'Use a password instead'}
            </Button>
          </form>
          </>
        )}

        <p className="mt-6 text-center text-meta text-muted">
          {t('auth.noAccount')}{' '}
          <Link to={signUpHref} className="text-ink underline underline-offset-4">
            {t('auth.createAccount')}
          </Link>
        </p>
      </>
    </AuthLayout>
  );
}
